import { parseMoneyInput } from '@fd/money';

/** More digits than any amount needs; the rest of a long paste is dropped. */
const MAX_DIGITS = 15;

/** The page's decimal mark for a typed amount: "," in Polish, "." in English (what parseMoneyInput reads). */
export const decimalMark = (locale: string): ',' | '.' => (locale.toLowerCase().startsWith('pl') ? ',' : '.');

/**
 * An amount typed as on a till ("Kwoty jak w kasie", Settings): the digits come in from the right
 * and the last `scale` of them are the pence, so 12568 is 125,68 and 5 is 0,05. Whatever the field
 * holds after a keystroke or a paste is read as its digits alone; a leading minus stays.
 */
export function cashText(typed: string, scale: number, decimal: ',' | '.'): string {
  const negative = /^\s*[-−]/.test(typed);
  const digits = typed.replace(/\D/g, '').replace(/^0+/, '').slice(0, MAX_DIGITS);
  if (digits === '') return negative ? '-' : '';
  const padded = digits.padStart(scale + 1, '0');
  const text = scale === 0 ? padded : `${padded.slice(0, -scale)}${decimal}${padded.slice(-scale)}`;
  return (negative ? '-' : '') + text;
}

/**
 * What a field already holding an amount shows once till entry takes it over: "120" becomes
 * "120,00", so the next digit lands in the pence. Text that is not an amount stays as it is.
 */
export function cashShown(value: string, currency: string, scale: number, locale: string): string {
  if (value.trim() === '') return '';
  const parsed = parseMoneyInput(value, currency, locale);
  if (!parsed.ok) return value;
  const negative = parsed.minor < 0n;
  return cashText(`${negative ? '-' : ''}${negative ? -parsed.minor : parsed.minor}`, scale, decimalMark(locale));
}
